// Short-leave "permissions" (a few hours off within a working day).
// There is no permissions endpoint yet, so requests are kept in this browser, per entity. Each one is
//   { id, employeeCode, employeeName, role, date (yyyy-MM-dd), from (HH:mm), to (HH:mm), reason,
//     status: 'pending' | 'approved' | 'rejected', createdOn, decidedBy, decidedOn, note }
const key = (entityId) => `ara.leave.permissions.${entityId ?? 'all'}`;

export const readPermissions = (entityId) => {
    try { const v = JSON.parse(window.localStorage.getItem(key(entityId)) || '[]'); return Array.isArray(v) ? v : []; } catch { return []; }
};
const write = (entityId, list) => {
    try { window.localStorage.setItem(key(entityId), JSON.stringify(list)); } catch { /* storage unavailable */ }
    return list;
};

// `marked` is for an admin recording a permission straight onto an employee: it is saved already approved.
export const addPermission = (entityId, req, marked = false) => {
    const now = new Date().toISOString();
    const item = {
        ...req, id: `perm-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        status: marked ? 'approved' : 'pending', createdOn: now,
        decidedBy: marked ? (req.markedBy || '') : '', decidedOn: marked ? now : '', note: '',
    };
    return write(entityId, [item, ...readPermissions(entityId)]);
};

export const decidePermission = (entityId, id, status, decidedBy, note = '') => write(
    entityId,
    readPermissions(entityId).map((p) => (p.id === id ? { ...p, status, decidedBy, decidedOn: new Date().toISOString(), note } : p)),
);

export const permissionStoreKey = key;

const mins = (hhmm) => { const [h, m] = String(hhmm || '').split(':').map(Number); return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null; };
export const permissionMinutes = (from, to) => { const a = mins(from); const b = mins(to); return a === null || b === null || b <= a ? 0 : b - a; };
export const fmtDuration = (m) => (m >= 60 ? `${Math.floor(m / 60)} hr${m % 60 ? ` ${m % 60} min` : ''}` : `${m} min`);
export const fmtTime12 = (hhmm) => {
    const a = mins(hhmm);
    if (a === null) return '—';
    const h = Math.floor(a / 60);
    return `${String(h % 12 || 12).padStart(2, '0')}:${String(a % 60).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};
export const fmtPermDate = (iso) => {
    const d = new Date(`${iso}T00:00:00`);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ---- permission-hour limits (set by HR). A list of rules:
//   { id, hours, period: 'month' | 'year', departments: [names] }   (no departments = every department)
// For a department the rule naming it wins over the all-departments rule; no rule means no limit.
const policyKey = (entityId) => `ara.leave.permissionPolicy.${entityId ?? 'all'}`;
export const EMPTY_POLICY = { rules: [] };

export const readPolicy = (entityId) => {
    try {
        const v = JSON.parse(window.localStorage.getItem(policyKey(entityId)) || 'null');
        return { rules: Array.isArray(v?.rules) ? v.rules : [] };
    } catch { return EMPTY_POLICY; }
};
export const writePolicy = (entityId, policy) => {
    try { window.localStorage.setItem(policyKey(entityId), JSON.stringify(policy)); } catch { /* storage unavailable */ }
    return policy;
};

export const resolveLimit = (policy, code, dept) => {
    const pick = (period) => {
        const rules = (policy.rules || []).filter((r) => r.period === period);
        const own = dept ? rules.filter((r) => (r.departments || []).includes(dept)).pop() : null;
        const all = rules.filter((r) => !(r.departments || []).length).pop();
        const r = own || all;
        return r ? r.hours : null;
    };
    return { month: pick('month'), year: pick('year') };
};

// Minutes already taken (pending + approved) in the month and the calendar year of `dateIso`.
export const usedMinutes = (list, code, dateIso) => {
    const ym = String(dateIso || '').slice(0, 7);
    const y = String(dateIso || '').slice(0, 4);
    return list.reduce((acc, p) => {
        if (p.employeeCode !== code || p.status === 'rejected') return acc;
        const m = permissionMinutes(p.from, p.to);
        return { month: acc.month + (String(p.date).slice(0, 7) === ym ? m : 0), year: acc.year + (String(p.date).slice(0, 4) === y ? m : 0) };
    }, { month: 0, year: 0 });
};
export const fmtHours = (h) => (h === null || h === undefined || h === '' ? 'No limit' : `${Number(h)} hr`);
